import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/footer.default.js';
import * as wireframeSkin from './skins/footer.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

class RunFooter extends RunElement {
  #compact = false;
  #rendering = false;

  static get observedAttributes() {
    return ['compact'];
  }

  _onSlotChangeBound = (e) => this.#handleSlotChange(e);

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'contentinfo');
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.shadowRoot) {
      this.shadowRoot.querySelectorAll('slot[name]').forEach(slot => {
        slot.removeEventListener('slotchange', this._onSlotChangeBound);
      });
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'compact':
        this.compact = newValue !== null;
        break;
    }
  }

  get compact() { return this.#compact; }
  set compact(value) {
    const coerced = Boolean(value);
    if (this.#compact === coerced) return;
    this.#compact = coerced;
    this.toggleAttribute('compact', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
      this.shadowRoot.querySelectorAll('slot[name]').forEach(slot => {
        slot.removeEventListener('slotchange', this._onSlotChangeBound);
        slot.addEventListener('slotchange', this._onSlotChangeBound);
      });
    } finally {
      this.#rendering = false;
    }
    this.#updateAria();
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  #updateAria() {
    if (this.#compact) {
      this.setAttribute('data-compact', '');
    } else {
      this.removeAttribute('data-compact');
    }
  }

  #handleSlotChange(event) {
    const slot = event.target;
    const assigned = slot.assignedElements({ flatten: true });
    this.toggleAttribute(`data-has-${slot.name}`, assigned.length > 0);
  }
}

if (!customElements.get('run-footer')) {
  customElements.define('run-footer', RunFooter);
}

export { RunFooter };
export default RunFooter;